import PublicLayout from "@/components/PublicLayout";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Cross, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const from = location.state?.from?.pathname || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(form.email, form.password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back. Peace be with you.");
      navigate(from, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <PublicLayout hideFooter>
      <section className="celestial-bg min-h-[calc(100vh-4rem)] grid place-items-center px-5 lg:px-8 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <span className="inline-flex w-12 h-12 rounded-full bg-gradient-to-br from-brand-sky to-brand-sage text-white items-center justify-center mb-4 shadow-sm">
              <Cross className="w-5 h-5" strokeWidth={1.6} />
            </span>
            <h1 className="font-serif text-4xl tracking-tight text-brand-ink">Welcome home.</h1>
            <p className="text-brand-quiet mt-1">Sign in to continue your journey.</p>
          </div>

          <form
            onSubmit={submit}
            className="bg-white rounded-2xl border border-brand-line p-7 space-y-4 soft-shadow"
            data-testid="login-form"
          >
            <div>
              <label className="block text-xs uppercase tracking-wider text-brand-quiet mb-1.5">Email</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="login-email-input"
                className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-4 py-3 text-brand-ink"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-brand-quiet mb-1.5">Password</label>
              <input
                required
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="login-password-input"
                className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-4 py-3 text-brand-ink"
              />
            </div>

            {error && (
              <p
                data-testid="login-error-message"
                className="text-sm text-[#9a4a3f] bg-[#fbeae6] border border-[#f1cfc6] rounded-lg px-3 py-2"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-colors px-6 py-3 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4" strokeWidth={1.8} /></>)}
            </button>

            <div className="flex items-center justify-between pt-2 text-sm">
              <Link
                to="/forgot-password"
                data-testid="login-forgot-link"
                className="text-brand-quiet hover:text-brand-skyDark"
              >
                Forgot password?
              </Link>
              <Link
                to="/register"
                data-testid="link-to-register"
                className="text-brand-skyDark hover:underline"
              >
                Begin your journey
              </Link>
            </div>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
}
